// organizationService.js
import { registerOrganization } from "../slices/organizationSlice";
import { logErrorEvent } from "../services/logService";

/**
 * Fetches an organization by ID:
 * - Checks the Redux store first
 * - If not present, fetches from FHIR server and registers it
 * - Logs an error AuditEvent if fetch fails
 *
 * @param {string} organizationId
 * @param {object} fhirClient
 * @param {function} getState
 * @param {function} dispatch
 * @param {object} store - Redux store instance
 * @returns {object|null} Organization resource or null if not found
 */
export const fetchOrganization = async (organizationId, fhirClient, store, { forceFetch = false } = {}) => {
  if (!organizationId || !fhirClient) return null;

  const cached = store.getState().organizations?.[organizationId];
  if (cached && !forceFetch) {
    // console.log("returning cached organization");
    return cached;
  }

  try {
    const fetched = await fhirClient.request(`Organization/${organizationId}`);
    store.dispatch(registerOrganization(fetched));
    return fetched;
  } catch (err) {
    console.error("❌ Failed to fetch organization:", err);
    const status = err?.response?.status || err?.status || 500;
    logErrorEvent({
      errorType: "read",
      error: {
        message: err?.message || "Organization fetch failed",
        status,
        resourceId: `Organization/${organizationId}`
      },
      description: `Failed to retrieve Organization/${organizationId} from FHIR server`,
      store
    });


    return { error: status };
  }
};

/**
 * Fetches an organization by reference string (e.g., "Organization/abc123")
 * - Validates format
 * - Extracts ID and delegates to fetchOrganization
 *
 * @param {string} ref
 * @param {object} fhirClient
 * @param {function} getState
 * @param {function} dispatch
 * @param {object} store - Redux store instance
 * @returns {object|null} Organization resource or null if not found
 */
export const fetchOrganizationByReference = async (ref, fhirClient, store,  options = {}) => {
  if (!ref || !ref.startsWith("Organization/") || ref === "Organization/undefined") {
    console.warn("⚠️ Invalid organization reference:", ref);
    return null;
  }

  const organizationId = ref.split("/")[1];
  if (!organizationId) {
    console.warn("⚠️ Could not extract organization ID from reference:", ref);
    return null;
  }

  return fetchOrganization(organizationId, fhirClient, store, options);
};
